import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { ApiError } from '@/shared/services/api-error';
import {
  requisitosDaSenha,
  type RequisitoSenha,
} from '@/modules/users/models/user.model';
import {
  forgotPasswordSchema,
  newPasswordSchema,
  twoFactorCodeSchema,
  type ForgotPasswordInput,
  type NewPasswordInput,
  type TwoFactorCodeInput,
} from '../models/auth.model';
import * as authService from '../services/auth.service';

export type ForgotPasswordStep = 'email' | 'codigo' | 'senha' | 'concluido';

export interface ForgotPasswordViewModel {
  step: ForgotPasswordStep;

  emailForm: UseFormReturn<ForgotPasswordInput>;
  codeForm: UseFormReturn<TwoFactorCodeInput>;
  passwordForm: UseFormReturn<NewPasswordInput>;

  /** E-mail informado no passo 1 — só para exibir "enviamos para X". */
  email: string;
  /** Requisitos da nova senha, item a item, mesmo indicador do cadastro de usuário. */
  requisitos: RequisitoSenha[];

  submitEmail: () => void;
  submitCode: () => void;
  submitPassword: () => void;
  voltarParaEmail: () => void;

  isSubmitting: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Recuperação de senha — código por e-mail, no padrão Cognito.
 *
 * Três passos em sequência: pedir o código, verificá-lo, definir a senha nova.
 * O `token` de cada passo é opaco e fica só em memória — nunca aparece na URL
 * nem persiste além da vida do componente.
 */
export function useForgotPasswordViewModel(defaultEmail = ''): ForgotPasswordViewModel {
  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState(defaultEmail);
  const [codeToken, setCodeToken] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const emailForm = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: defaultEmail },
    mode: 'onBlur',
  });

  const codeForm = useForm<TwoFactorCodeInput>({
    resolver: zodResolver(twoFactorCodeSchema),
    defaultValues: { code: '' },
    mode: 'onBlur',
  });

  const passwordForm = useForm<NewPasswordInput>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '', passwordConfirmation: '' },
    mode: 'onBlur',
  });

  const senha = passwordForm.watch('password');

  const submitEmail = emailForm.handleSubmit(async (valores) => {
    setError(null);
    try {
      const resultado = await authService.forgotPassword(valores.email);
      setEmail(valores.email);
      setCodeToken(resultado.token);
      setStep('codigo');
    } catch (falha) {
      setError(
        falha instanceof ApiError ? falha.message : 'Não foi possível enviar o código. Tente novamente.',
      );
    }
  });

  const submitCode = codeForm.handleSubmit(async (valores) => {
    setError(null);
    try {
      const resultado = await authService.verifyResetCode(codeToken, valores.code);
      setResetToken(resultado.resetToken);
      setStep('senha');
    } catch (falha) {
      setError(falha instanceof ApiError ? falha.message : 'Código incorreto. Tente novamente.');
    }
  });

  const submitPassword = passwordForm.handleSubmit(async (valores) => {
    setError(null);
    try {
      await authService.resetPassword(resetToken, valores.password, valores.passwordConfirmation);
      setStep('concluido');
    } catch (falha) {
      setError(
        falha instanceof ApiError ? falha.message : 'Não foi possível redefinir a senha. Tente novamente.',
      );
    }
  });

  const isSubmitting =
    emailForm.formState.isSubmitting || codeForm.formState.isSubmitting || passwordForm.formState.isSubmitting;

  return {
    step,
    emailForm,
    codeForm,
    passwordForm,
    email,
    requisitos: senha ? requisitosDaSenha(senha) : [],

    submitEmail: () => void submitEmail(),
    submitCode: () => void submitCode(),
    submitPassword: () => void submitPassword(),
    voltarParaEmail: () => {
      setError(null);
      setStep('email');
    },

    isSubmitting,
    error,
    reset: () => {
      setStep('email');
      setEmail(defaultEmail);
      setCodeToken('');
      setResetToken('');
      setError(null);
      emailForm.reset({ email: defaultEmail });
      codeForm.reset({ code: '' });
      passwordForm.reset({ password: '', passwordConfirmation: '' });
    },
  };
}
